import React, { createContext, useContext } from 'react';
import useFetchData from '../../hooks/useFetchData';
import ConnectriaTicketsService from '../../services/ConnectriaTicketsService';
import YetiService from '../../services/YetiServices';
import YetiTicketProvider from '../../services/YetiTickets';
import { PROVIDERS } from './constants';



const fetchAttachments = async (provider, ticketId, cancelToken) => {
  try {
    switch (provider) {
      case PROVIDERS.CONNECTRIA:
        return await ConnectriaTicketsService.ticketAttachments(ticketId);

      case PROVIDERS.LIGHTEDGE: {
        const attachmentsResponse = await YetiTicketProvider.getTicketAttachments(ticketId, { cancelToken });
        return attachmentsResponse?.data?.results?.map((attachment) =>
          YetiTicketProvider.parseTicketAttachment(attachment, ticketId)
        );
      }

      default:
        throw new Error(`Unknown provider (${provider}) for ticket (${ticketId})`);
    }
  } catch (error) {
    throw error;
  }
};

const fetchComments = async (provider, ticketId, cancelToken) => {
  try {
    switch (provider) {
      case PROVIDERS.CONNECTRIA: {
        const [history, ratings] = await Promise.all([
          ConnectriaTicketsService.ticketHistory(ticketId, { cancelToken }),
          ConnectriaTicketsService.getTicketCommentRatings(ticketId, { cancelToken }),
        ]);
        const comments = (history || []).filter((comment) => !comment.pvt);
        return { comments, ratings };
      }

      case PROVIDERS.LIGHTEDGE: {
        const commentsResponse = await YetiTicketProvider.getTicketComments(ticketId, { cancelToken });
        const parsedComments =
          commentsResponse?.data?.results?.map((comment) =>
            YetiTicketProvider.parseTicketComment(comment)
          ) || [];
        return {
          comments: parsedComments,
          ratings: []
        };
      }

      default:
        throw new Error(`Unknown provider (${provider}) for ticket (${ticketId})`);
    }
  } catch (error) {
    throw error;
  }
};

const fetchTicketDetails = async (provider, ticketId, cancelToken) => {
  try {
    switch (provider) {
      case PROVIDERS.CONNECTRIA:
        return ConnectriaTicketsService.ticketDetail(ticketId, { cancelToken });

      case PROVIDERS.LIGHTEDGE: {
        const [ticketResponse, recipientsResponse, servicesResponse] = await Promise.all([
          YetiTicketProvider.getTicket(ticketId, { cancelToken }),
          YetiTicketProvider.getTicketRecipients(ticketId),
          YetiService.serviceList(),
        ]);

        const ticket = YetiTicketProvider.parseTicket(ticketResponse.data.results);
        const recipients = recipientsResponse.data.results || [];
        const services = servicesResponse.data.results || [];

        const service = services.find((service) => service.id === ticket.device_id);

        return { ...ticket, recipients, service };
      }

      default:
        return {};
    }
  } catch (error) {
    console.error('Error fetching ticket details:', error);
    throw error;
  }
};


const TicketDataContext = createContext();
export const useTicketData = () => useContext(TicketDataContext);

export const TicketDataProvider = ({ ticketId, provider, children }) => {
  const {
    data: attachments = [],
    isLoading: isLoadingAttachments,
    error: attachmentsError,
    revalidate: revalidateAttachments,
  } = useFetchData(
    (cancelToken) => fetchAttachments(provider, ticketId, cancelToken),
    [provider, ticketId]
  );

  const {
    data: commentData = { comments: [], ratings: [] },
    isLoading: isLoadingComments,
    error: commentsError,
    revalidate: revalidateComments,
  } = useFetchData(
    (cancelToken) => fetchComments(provider, ticketId, cancelToken),
    [provider, ticketId]
  );

  const {
    data: ticketDetails = {},
    isLoading: isLoadingTicketDetails,
    error: ticketDetailsErrors,
    revalidate: revalidateTicketDetails,
  } = useFetchData(
    (cancelToken) => fetchTicketDetails(provider, ticketId, cancelToken),
    [provider, ticketId]
  );

  const comments = commentData?.comments || [];
  const ratings = commentData?.ratings || [];

  // This page error catches all errors,
  // then based on the status you can customize which error message you would like to throw on the page.
  // make sure to grab the first error with 401 to ensure it gets rendered first if user is on unauthorized page. 
 const pageError = 
  [ticketDetailsErrors, attachmentsError, commentsError].find(e => e?.response?.status === 401) ||
  attachmentsError ||
  commentsError ||
  ticketDetailsErrors
 
  return (
    <TicketDataContext.Provider
      value={{
        attachments,
        attachmentsError,
        comments,
        commentsError,
        isLoadingAttachments,
        isLoadingComments,
        isLoadingTicketDetails,
        provider,
        ratings,
        revalidateAttachments,
        revalidateComments,
        revalidateTicketDetails,
        ticketDetails,
        ticketDetailsErrors,
        pageError,
      }}
    >
      {children}
    </TicketDataContext.Provider>
  );
};